/**
 * ORG-EDITOR.1+2 (2026-06-04): українські класифікатори для OrganizationEditor.
 *
 * 1. OPFG_CODES — Класифікатор організаційно-правових форм господарювання
 *    ДК 002:2004. Найпоширеніші ~30 кодів (повний — 80+, рідкісні відсутні).
 *
 * 2. IBAN bank lookup — НЕ hardcoded мапа (як було у MVP), а через
 *    довідник у БД `ref.banks` (seed з NBU API, 58 активних банків).
 *    Endpoint: GET /refs/banks. Frontend кешує через React Query.
 *
 *    Алгоритм: український IBAN формат "UA__ XXXXXX YYYYYYYYYYYYYYYYYYYY"
 *    де XXXXXX (6 цифр з позиції 5-10 — МФО банку, він же ID у NBU).
 *    Приклад: UA713395000000026008370230001 → МФО=339500 (ТАСКОМБАНК).
 *
 *    Власник 2026-06-04: hardcoded TS-таблиця була **помилковою** —
 *    я приписав 339500 = Мегабанк (реально ТАСКОМБАНК), 300012 =
 *    monobank (реально ліквідований Промінвест). Заміна на DB-driven
 *    джерело з оновленням через NBU API.
 */

export interface BankRow {
  mfo: string;
  name: string;
  edrpou?: string | null;
}

export interface OpfgOption {
  code: string;   // код КОПФГ (ДК 002:2004)
  name: string;   // назва для картки (офіційна; у дужках — загальноприйняте скорочення)
  /** ч.1195: унікальний ключ вибору, коли один код має кілька назв
   *  (230 — ПрАТ/ПАТ/АТ); за замовчуванням = code. */
  key?: string;
}

/** ч.1195: ФОП у ДК 002:2004 — 910 «Підприємець-фізична особа» (280 — «Адвокатське обʼєднання»). */
export const FOP_OPFG_CODE = '910';
/** ч.747: псевдо-код «Фізична особа» для контрагентів — без оргформи у назвах. */
export const INDIVIDUAL_OPFG_CODE = 'FIZ';
export const INDIVIDUAL_OPFG: OpfgOption = { code: INDIVIDUAL_OPFG_CODE, name: 'Фізична особа' };

/** «Товариство з обмеженою відповідальністю (ТОВ)» → «ТОВ»; без дужок або з
 *  поясненням у дужках («Філія (інший відокремлений підрозділ)») — ''. */
export function opfDefaultShort(opfgName: string | null | undefined): string {
  const m = /\(([^)]+)\)\s*$/.exec(opfgName ?? '');
  const abbr = m?.[1]?.trim() ?? '';
  return /^\S{1,6}$/.test(abbr) ? abbr : '';
}

/** Повна форма ОПФГ великими без скорочення у дужках. */
export function opfFullForm(opfgName: string | null | undefined): string {
  return (opfgName ?? '').replace(/\s*\([^)]+\)\s*$/, '').trim().toUpperCase();
}

export function isFopOpfg(code: string | null | undefined, name?: string | null): boolean {
  // «Фізична особа-підприємець» — ФОП; проста «Фізична особа» (FIZ) — ні.
  return code === FOP_OPFG_CODE || /підприємець/i.test(name ?? '');
}

/**
 * ч.1192: проєкт короткої назви, коли «Скорочено» порожнє, — ОДНА функція і
 * для підказки у полі, і для значення за замовчуванням (раніше підказка
 * казала «ТОВ «Ромашка»», а назва ставала «Ромашка»). ДЗЕРКАЛО backend
 * `services/org_names.py:default_short_name`. '' — якщо власна назва порожня.
 */
export function defaultShortName(p: {
  opfg_code: string | null | undefined; opfg_name: string | null | undefined;
  own_name: string | null | undefined;
}): string {
  const own = (p.own_name ?? '').trim().replace(/\s+/g, ' ');
  if (!own) return '';
  if (isFopOpfg(p.opfg_code, p.opfg_name)) return `ФОП ${own}`;
  if (p.opfg_code === INDIVIDUAL_OPFG_CODE) return own;
  const abbr = opfDefaultShort(p.opfg_name);
  return abbr ? `${abbr} «${own}»` : own;
}

/**
 * ч.746: назви організації з оргформи + власної назви — ДЗЕРКАЛО
 * backend `services/org_names.py:compile_org_names` (бекенд авторитетний,
 * тут — live-прев'ю). null, якщо власна назва порожня.
 */
export function compileOrgNames(p: {
  opfg_code: string | null; opfg_name: string | null;
  own_name: string | null; opf_short: string | null;
}): { name: string; full_name: string } | null {
  const own = (p.own_name ?? '').trim().replace(/\s+/g, ' ');
  if (!own) return null;
  // «Скорочено» заповнене → коротка назва = САМЕ його текст (уточнення
  // власника 2026-09-09); порожнє → проєкт з defaultShortName (ч.1192).
  const short = (p.opf_short ?? '').trim().replace(/\s+/g, ' ');
  const fop = isFopOpfg(p.opfg_code, p.opfg_name);
  const name = short || defaultShortName({ ...p, own_name: own });
  if (fop) return { name, full_name: `ФІЗИЧНА ОСОБА-ПІДПРИЄМЕЦЬ ${own}` };
  const full = p.opfg_code === INDIVIDUAL_OPFG_CODE ? '' : opfFullForm(p.opfg_name);
  return { name, full_name: full ? `${full} «${own}»` : own };
}

/**
 * Legacy-картка (own_name порожній): спробувати вивести власну назву та
 * скорочення з наявних name/full_name + оргформи. Не змінює БД — лише
 * заповнює поля форми; збережеться тільки після «зберегти».
 */
export function deriveOwnName(p: {
  name: string; full_name: string; opfg_code: string | null; opfg_name: string | null;
}): { own_name: string; opf_short: string; opfg_code: string | null; opfg_name: string | null } {
  const strip = (v: string) => v.replace(/^[«"“„'\s]+|[»"”'\s]+$/g, '').trim();
  let code = p.opfg_code, oname = p.opfg_name;
  if (!code) {
    // впізнати оргформу за префіксом повної назви
    const fu = p.full_name.trim().toUpperCase();
    const hit = OPFG_CODES.find((o) => fu.startsWith(opfFullForm(o.name) + ' '));
    if (hit) { code = hit.code; oname = hit.name; }
  }
  // «Скорочено» = наявна коротка назва цілком (юзер її і так писав сам)
  const short = p.name.trim();
  if (isFopOpfg(code, oname)) {
    const own = strip(p.full_name.replace(/^фізична особа[-\s]підприємець/i, '')) || strip(p.name.replace(/^ФОП\s+/i, ''));
    return { own_name: own, opf_short: short, opfg_code: code, opfg_name: oname };
  }
  const full = opfFullForm(oname);
  let own = p.full_name.trim();
  if (full && own.toUpperCase().startsWith(full)) own = own.slice(full.length);
  own = strip(own) || strip(p.name);
  return { own_name: own, opf_short: short, opfg_code: code, opfg_name: oname };
}

/**
 * ч.1195 (звернення SUP-A034704E): оргформи — ПОВНИЙ офіційний КОПФГ
 * (ДК 002:2004 зі Зміною №10, наказ Мінекономіки від 07.02.2025 № 1001; джерело —
 * таблиця Держстату kopfg_xls.xlsx). До ч.1195 список був «топ-30» з вигаданими
 * кодами (280 «ФОП», 410 «Державне підприємство», 425 «Орган виконавчої
 * влади»…) і без органів влади — тестувальниця не знала, куди внести митницю й
 * податкову. Назви — офіційні; у дужках — загальноприйняте скорочення (воно
 * стає проєктом короткої назви: opfDefaultShort). Для 230 три варіанти назви
 * (ПрАТ/ПАТ/АТ) — ключ вибору `key`, код один і той самий, як у ЄДР.
 * Форми з позначкою «*)» у класифікаторі (нові не реєструються) лишаємо —
 * такі контрагенти ще існують.
 */
const OPFG_OFFICIAL: OpfgOption[] = [
  // 100 Підприємства
  { code: '110', name: 'Фермерське господарство (ФГ)' },
  { code: '120', name: 'Приватне підприємство (ПП)' },
  { code: '130', name: 'Колективне підприємство' },
  { code: '140', name: 'Державне підприємство (ДП)' },
  { code: '145', name: 'Казенне підприємство' },
  { code: '150', name: 'Комунальне підприємство (КП)' },
  { code: '151', name: 'Спільне комунальне підприємство' },
  { code: '160', name: 'Дочірнє підприємство' },
  { code: '170', name: 'Іноземне підприємство' },
  { code: '180', name: 'Підприємство обʼєднання громадян (релігійної організації, профспілки)' },
  { code: '185', name: 'Підприємство споживчої кооперації' },
  { code: '190', name: 'Орендне підприємство' },
  { code: '191', name: 'Індивідуальне підприємство' },
  { code: '192', name: 'Сімейне підприємство' },
  { code: '193', name: 'Спільне підприємство' },
  // 200 Господарські товариства
  { code: '230', key: '230:prat', name: 'Приватне акціонерне товариство (ПрАТ)' },
  { code: '230', key: '230:pat', name: 'Публічне акціонерне товариство (ПАТ)' },
  { code: '230', name: 'Акціонерне товариство (АТ)' },
  { code: '231', name: 'Відкрите акціонерне товариство (ВАТ)' },
  { code: '232', name: 'Закрите акціонерне товариство (ЗАТ)' },
  { code: '235', name: 'Державна акціонерна компанія (товариство)' },
  { code: '236', name: 'Державна холдингова компанія' },
  { code: '237', name: 'Холдингова компанія' },
  { code: '240', name: 'Товариство з обмеженою відповідальністю (ТОВ)' },
  { code: '250', name: 'Товариство з додатковою відповідальністю (ТДВ)' },
  { code: '260', name: 'Повне товариство' },
  { code: '270', name: 'Командитне товариство' },
  { code: '280', name: 'Адвокатське обʼєднання' },
  { code: '281', name: 'Адвокатське бюро' },
  // 300 Кооперативи
  { code: '310', name: 'Виробничий кооператив' },
  { code: '320', name: 'Обслуговуючий кооператив' },
  { code: '321', name: 'Житлово-будівельний кооператив (ЖБК)' },
  { code: '322', name: 'Гаражний кооператив' },
  { code: '330', name: 'Споживчий кооператив' },
  { code: '331', name: 'Споживче товариство' },
  { code: '340', name: 'Сільськогосподарський виробничий кооператив' },
  { code: '350', name: 'Сільськогосподарський обслуговуючий кооператив' },
  { code: '360', name: 'Сільськогосподарський кооператив' },
  { code: '370', name: 'Сільськогосподарське кооперативне обʼєднання' },
  { code: '390', name: 'Кооперативний банк' },
  // 400 Організації (установи, заклади)
  { code: '410', name: 'Орган державної влади' },
  { code: '415', name: 'Суди' },
  { code: '420', name: 'Орган місцевого самоврядування' },
  { code: '425', name: 'Державна організація (установа, заклад)' },
  { code: '430', name: 'Комунальна організація (установа, заклад)' },
  { code: '435', name: 'Приватна організація (установа, заклад)' },
  { code: '440', name: 'Організація (установа, заклад) обʼєднання громадян (релігійної організації, профспілки, споживчої кооперації тощо)' },
  { code: '490', name: 'Організація орендарів' },
  { code: '495', name: 'Організація покупців' },
  // 500 Обʼєднання підприємств (юридичних осіб)
  { code: '510', name: 'Асоціація' },
  { code: '520', name: 'Корпорація' },
  { code: '530', name: 'Консорціум' },
  { code: '540', name: 'Концерн' },
  { code: '560', name: 'Спілка споживчих товариств' },
  { code: '590', name: 'Інші обʼєднання юридичних осіб' },
  // 600 Відокремлені підрозділи без статусу юридичної особи
  { code: '610', name: 'Філія (інший відокремлений підрозділ)' },
  { code: '620', name: 'Представництво' },
  // 700 Непідприємницькі товариства
  { code: '710', name: 'Органи адвокатського самоврядування' },
  { code: '711', name: 'Органи суддівського самоврядування' },
  { code: '712', name: 'Вища кваліфікаційна комісія суддів України' },
  { code: '720', name: 'Фонди соціального страхування' },
  { code: '750', name: 'Саморегулівні організації' },
  { code: '760', name: 'Асоціації органів місцевого самоврядування та їх добровільні обʼєднання' },
  { code: '770', name: 'Торгово-промислова палата' },
  { code: '780', name: 'Аудиторська палата України' },
  { code: '790', name: 'Організація водокористувачів' },
  // 800 Громадські обʼєднання, профспілки, благодійні організації
  { code: '810', name: 'Політична партія' },
  { code: '815', name: 'Громадська організація (ГО)' },
  { code: '820', name: 'Громадська спілка' },
  { code: '825', name: 'Релігійна організація' },
  { code: '830', name: 'Профспілка' },
  { code: '835', name: 'Обʼєднання профспілок' },
  { code: '840', name: 'Творча спілка (інша професійна організація)' },
  { code: '845', name: 'Благодійна організація (БО)' },
  { code: '850', name: 'Організація роботодавців' },
  { code: '855', name: 'Обʼєднання співвласників багатоквартирного будинку (ОСББ)' },
  { code: '860', name: 'Орган самоорганізації населення' },
  // 900 Інші організаційно-правові форми
  { code: '910', name: 'Фізична особа-підприємець (ФОП)' },
  { code: '915', name: 'Товарна біржа' },
  { code: '925', name: 'Кредитна спілка' },
  { code: '940', name: 'Недержавний пенсійний фонд' },
  { code: '950', name: 'Садівниче товариство' },
  { code: '995', name: 'Інші організаційно-правові форми' },
];

/** Ключ вибору у списку (унікальний; = code, крім варіантів назв одного коду). */
export function opfgKey(o: OpfgOption): string {
  return o.key ?? o.code;
}

/** Найуживаніші — першими у списку; далі решта в порядку класифікатора. */
const POPULAR_KEYS = ['240', '910', '120', '230:prat', '230:pat', '250', '140', '150', '110',
  '815', '845', '855', '410', '420', '425', '430', '610', '620'];
export const OPFG_CODES: OpfgOption[] = [
  ...POPULAR_KEYS.map((k) => OPFG_OFFICIAL.find((o) => opfgKey(o) === k)!),
  ...OPFG_OFFICIAL.filter((o) => !POPULAR_KEYS.includes(opfgKey(o))),
];

/** ч.747: список оргформ для КОНТРАГЕНТА — першим «Фізична особа». */
export const CONTRACTOR_OPFG_CODES: OpfgOption[] = [INDIVIDUAL_OPFG, ...OPFG_CODES];

/** Опція за ключем вибору (ключ = code або '230:prat'); серед контрагентських — і 'FIZ'. */
export function findOpfgByKey(key: string | null | undefined): OpfgOption | null {
  if (!key) return null;
  return CONTRACTOR_OPFG_CODES.find((o) => opfgKey(o) === key) ?? null;
}

/** Ключ вибору для збережених (code, name): варіант назви того самого коду
 *  (ПрАТ/ПАТ/АТ) — за назвою; невідома пара — перший варіант коду; нема — ''. */
export function opfgKeyFor(code: string | null | undefined, name?: string | null): string {
  if (!code) return '';
  const same = CONTRACTOR_OPFG_CODES.filter((o) => o.code === code);
  if (same.length === 0) return '';
  const first = same[0];
  if (!first) return '';
  const byName = name ? same.find((o) => o.name === name) : undefined;
  return opfgKey(byName ?? first);
}

/** Знайти OPFG-name за code (перший варіант назви коду). */
export function findOpfgName(code: string | null | undefined): string | null {
  if (!code) return null;
  const entry = OPFG_CODES.find((o) => o.code === code);
  return entry ? entry.name : null;
}

/**
 * Витягти МФО з українського IBAN. Формат UA__ MFO(6) ACCOUNT(19).
 * Повертає null якщо IBAN некоректний або не український.
 */
export function extractMfoFromIban(iban: string | null | undefined): string | null {
  if (!iban) return null;
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  // UA + 2 контр.цифри + 6 цифр МФО + 19 цифр рахунку = 29 символів
  if (!/^UA\d{27}$/.test(clean)) return null;
  return clean.slice(4, 10);
}

/**
 * Знайти назву банку за IBAN, використовуючи список банків з БД.
 * NULL якщо невідомий банк / некоректний IBAN / список ще не завантажено.
 */
export function findBankByIban(
  iban: string | null | undefined,
  banks: BankRow[],
): string | null {
  const mfo = extractMfoFromIban(iban);
  if (!mfo) return null;
  const bank = banks.find((b) => b.mfo === mfo);
  return bank ? bank.name : null;
}

/**
 * Format IBAN з пробілами:  UA71 339500 00000 2600 8370 2300 01
 * Маска (НБУ-стиль): 4 (UA+КС) + 6 (МФО) + 5 + 4 + 4 + 4 + 2 = 29 символів.
 * Це звичний банк/бух-формат — бухгалтер бачить МФО окремою групою.
 */
export function formatIban(iban: string | null | undefined): string {
  if (!iban) return '';
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  if (clean.length === 0) return '';
  // Якщо ще не повний IBAN — просто додаємо пробіли по 4 для зручності вводу
  if (clean.length < 29) {
    return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
  }
  // Маска 4-6-5-4-4-4-2 для повного UA IBAN (29 символів)
  return [
    clean.slice(0, 4),    // UA + 2 контр.цифри (4)
    clean.slice(4, 10),   // МФО (6)
    clean.slice(10, 15),  // 5
    clean.slice(15, 19),  // 4
    clean.slice(19, 23),  // 4
    clean.slice(23, 27),  // 4
    clean.slice(27, 29),  // 2
  ].join(' ');
}

/** Валідація українського IBAN — формат UA + 27 цифр. Без mod-97 checksum. */
export function isValidUaIban(iban: string | null | undefined): boolean {
  if (!iban) return false;
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  return /^UA\d{27}$/.test(clean);
}
